import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { fetchCart } from '../store/slices/cartSlice';
import { handleSessionExpiry } from '../store/middleware/sessionMiddleware';
import { getMsUntilExpiry } from '../lib/utils/jwt';

interface AppInitializerProps {
  children: React.ReactNode;
}

/**
 * App-level setup: loads the cart and watches the session clock.
 */
export default function AppInitializer({ children }: AppInitializerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  // The cart is guest-capable (X-Cart-Token), so load it either way.
  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch, isAuthenticated]);

  /**
   * End the session the moment the token lapses instead of waiting for the next
   * request to 401. Without this, someone who leaves a tab open comes back to a
   * UI that looks signed in and fails on the first click they make.
   */
  useEffect(() => {
    if (!accessToken) return;

    const msLeft = getMsUntilExpiry(accessToken);
    if (msLeft === null) return; // No readable expiry - let the API decide.

    if (msLeft <= 0) {
      handleSessionExpiry(dispatch);
      return;
    }

    // setTimeout caps out around 24.8 days; clamp to stay well inside that.
    const delay = Math.min(msLeft, 2_147_483_000);
    const timer = setTimeout(() => handleSessionExpiry(dispatch), delay);
    return () => clearTimeout(timer);
  }, [accessToken, dispatch]);

  return <>{children}</>;
}
